import { Suspense, lazy, type ReactElement } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'

import { CompanyAccessesTab } from '@/features/accesses/CompanyAccessesTab'
import { CompanyTechStackTab } from '@/features/architecture/CompanyTechStackTab'
import { LoginPage } from '@/features/auth/LoginPage'
import { CompaniesPage } from '@/features/companies/CompaniesPage'
import { CompanyDetailPage } from '@/features/companies/CompanyDetailPage'
import { CompanyContactsTab } from '@/features/companies/components/CompanyContactsTab'
import { CompanyProjectsTab } from '@/features/companies/components/CompanyProjectsTab'
import { CompanySummaryTab } from '@/features/companies/components/CompanySummaryTab'
import { CompanyBriefsTab, CompanyDocumentsTab } from '@/features/docs/CompanyDocumentsTab'
import { DocumentRoute } from '@/features/docs/DocumentRoute'
import { DueSoonPage } from '@/features/due-soon/DueSoonPage'
import { MyTasksPage } from '@/features/due-soon/MyTasksPage'
import { PortalCompanyPage } from '@/features/portal/PortalCompanyPage'
import { PortalProjectDetailPage } from '@/features/portal/PortalProjectDetailPage'
import { PortalProjectsPage } from '@/features/portal/PortalProjectsPage'
import { MetricsPage } from '@/features/metrics/MetricsPage'
import { OnboardingPage } from '@/features/onboarding/OnboardingPage'
import { ProjectOnboardingTab } from '@/features/onboarding/components/ProjectOnboardingTab'
import { PortfolioPage } from '@/features/portfolio/PortfolioPage'
import { PrioritiesPage } from '@/features/priorities/PrioritiesPage'
import { ProjectDetailPage } from '@/features/projects/ProjectDetailPage'
import { TimelinePage } from '@/features/timeline/TimelinePage'
import { ProjectActivityTab } from '@/features/projects/components/ProjectActivityTab'
import { ProjectDocumentsTab } from '@/features/projects/components/ProjectDocumentsTab'
import { ProjectSummaryTab } from '@/features/projects/components/ProjectSummaryTab'
import { ProjectTasksTab } from '@/features/projects/components/ProjectTasksTab'
import { StageSettingsPage } from '@/features/settings/StageSettingsPage'
import { AgentsPage } from '@/features/settings/AgentsPage'
import { UsersPage } from '@/features/settings/UsersPage'
import { SupportPage } from '@/features/support/SupportPage'
import { SkeletonRows } from '@/shared/components/data'
import type { Role } from '@/shared/types/domain'

import { AuthLayout } from './layouts/AuthLayout'
import { InternalLayout } from './layouts/InternalLayout'
import { PortalLayout } from './layouts/PortalLayout'
import { ForbiddenPage, NotFoundPage, RouteErrorBoundary } from './routes/error-boundary'
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequireRole,
  RoleHomeRedirect,
} from './routes/guards'

/**
 * Tabla de rutas del plan (3.2).
 *
 * | Ruta                                                  | Rol                     |
 * |-------------------------------------------------------|-------------------------|
 * | /portfolio /priorities /board /companies /companies/:id/*
 *   /projects/:id/* /due-soon /me/tasks                   | admin, tech_member |
 * | /settings/{phases,task-statuses,users} /metrics       | admin              |
 * | /portal /portal/projects/:id /portal/company          | client          |
 *
 * TODAS existen desde el shell, con placeholders donde la pantalla todavia no
 * se construyo. El arbol `/portal/*` es fisicamente distinto: distinto layout,
 * distintas queries. Un `client` nunca monta un componente que sepa
 * pedir accesos.
 */

/**
 * El diagrama (React Flow + elkjs) pesa más que varias pantallas juntas y lo
 * abre una fracción de las visitas. Se carga cuando alguien entra a la tab, no
 * en el primer paint de todo el mundo.
 */
const ArchitectureTab = lazy(() =>
  import('@/features/architecture/ArchitectureTab').then((module) => ({
    default: module.ArchitectureTab,
  })),
)

const lazyTab = (element: ReactElement) => (
  <Suspense fallback={<SkeletonRows rows={6} withSpine={false} />}>{element}</Suspense>
)

const INTERNAL: readonly Role[] = ['admin', 'tech_member']
const ADMIN_ONLY: readonly Role[] = ['admin']
/** El admin entra a `/portal?asCompany=:id` para ver lo que ve el cliente. */
const PORTAL: readonly Role[] = ['client', 'admin']

const companyTabs: RouteObject[] = [
  { index: true, element: <Navigate to="resumen" replace /> },
  { path: 'resumen', element: <CompanySummaryTab /> },
  { path: 'proyectos', element: <CompanyProjectsTab /> },
  { path: 'contactos', element: <CompanyContactsTab /> },
  { path: 'accesos', element: <CompanyAccessesTab /> },
  { path: 'documentos', element: <CompanyDocumentsTab /> },
  // El documento se abre DENTRO de la ficha: el header y las tabs no se pierden.
  { path: 'documentos/:documentId', element: <DocumentRoute /> },
  { path: 'briefs', element: <CompanyBriefsTab /> },
  { path: 'briefs/:documentId', element: <DocumentRoute /> },
  {
    path: 'tech-stack',
    element: <CompanyTechStackTab />,
  },
]

const projectTabs: RouteObject[] = [
  { index: true, element: <Navigate to="resumen" replace /> },
  { path: 'resumen', element: <ProjectSummaryTab /> },
  { path: 'tareas', element: <ProjectTasksTab /> },
  { path: 'documentos', element: <ProjectDocumentsTab /> },
  { path: 'documentos/:documentId', element: <DocumentRoute /> },
  { path: 'onboarding', element: <ProjectOnboardingTab /> },
  { path: 'actividad', element: <ProjectActivityTab /> },
  // El tablero de tareas vive DENTRO de la tab Tareas, con el toggle
  // Lista | Tablero: es la misma lista, agrupada distinto.
  { path: 'tablero', element: <Navigate to="../tareas" replace /> },
  { path: 'arquitectura', element: lazyTab(<ArchitectureTab />) },
]

const internalRoutes: RouteObject[] = [
  { path: 'portfolio', element: <PortfolioPage /> },
  { path: 'priorities', element: <PrioritiesPage /> },
  { path: 'timeline', element: <TimelinePage /> },
  // El tablero se consolido dentro del portafolio: es la misma lista agrupada
  // por fase, y ahora hereda sus filtros. La URL vieja sigue resolviendo —
  // esta en links de Slack y en la memoria de la gente.
  { path: 'board', element: <Navigate to="/portfolio?vista=tablero" replace /> },
  { path: 'companies', element: <CompaniesPage /> },
  { path: 'companies/:companyId', element: <CompanyDetailPage />, children: companyTabs },
  { path: 'projects/:projectId', element: <ProjectDetailPage />, children: projectTabs },
  { path: 'due-soon', element: <DueSoonPage /> },
  { path: 'me/tasks', element: <MyTasksPage /> },
  { path: 'support', element: <SupportPage /> },
]

/**
 * La regla de las URLs: **sección en inglés, pestaña en castellano**.
 *
 * `/companies`, `/projects`, `/portfolio`, `/board` son secciones; `resumen`,
 * `contactos`, `tareas`, `documentos` son pestañas. El QA funcional (3.3) vio
 * la mezcla y la leyó como arbitraria, y en `settings` lo era: sus hijos eran
 * los únicos en inglés —`phases`, `task-statuses`, `users`— siendo pestañas
 * como las demás. Escribir `/settings/fases` daba un 404, que es el 404 que uno
 * se come por adivinar bien.
 *
 * Los paths viejos siguen funcionando con un `Navigate`: una URL que alguien
 * pudo haber guardado o compartido no se rompe porque nosotros corrijamos una
 * inconsistencia nuestra.
 */
const settingsRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="fases" replace /> },
  { path: 'fases', element: <StageSettingsPage kind="project" /> },
  { path: 'estados-de-tarea', element: <StageSettingsPage kind="task" /> },
  { path: 'usuarios', element: <UsersPage /> },
  { path: 'agentes', element: <AgentsPage /> },

  // Los de antes, redirigidos.
  { path: 'phases', element: <Navigate to="../fases" replace /> },
  { path: 'task-statuses', element: <Navigate to="../estados-de-tarea" replace /> },
  { path: 'users', element: <Navigate to="../usuarios" replace /> },
]

/**
 * El arbol del portal.
 *
 * Tres rutas y ninguna mas: el detalle de un proyecto resuelve sus secciones
 * con estado local en vez de con subrutas. Menos superficie que proteger.
 */
const portalRoutes: RouteObject[] = [
  { index: true, element: <PortalProjectsPage /> },
  { path: 'projects/:projectId', element: <PortalProjectDetailPage /> },
  { path: 'company', element: <PortalCompanyPage /> },
]

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: (
          <RedirectIfAuthenticated>
            <AuthLayout />
          </RedirectIfAuthenticated>
        ),
        children: [{ path: 'login', element: <LoginPage /> }],
      },

      { path: '/', element: <RoleHomeRedirect /> },

      /*
        LA ÚNICA RUTA SIN SESIÓN DE TODA LA APP.
        Va FUERA de `RequireAuth` a propósito: la abre alguien que todavía no
        tiene cuenta —el onboarding pasa justo antes de que la tenga— y la
        credencial es el token del link, no un login. No tiene layout: ni barra
        lateral, ni espina de empresa, ni ⌘K, para que se note de un vistazo que
        no es la app interna ni el portal.
      */
      { path: 'onboarding/:token', element: <OnboardingPage /> },

      // --- Interno ------------------------------------------------------
      {
        element: <RequireAuth />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            element: (
              <RequireRole allow={INTERNAL}>
                <InternalLayout />
              </RequireRole>
            ),
            children: [
              ...internalRoutes,
              {
                path: 'settings',
                element: <RequireRole allow={ADMIN_ONLY} />,
                children: settingsRoutes,
              },
              // El tablero de métricas mide al equipo entero, persona por
              // persona: es de quien gestiona. El backend contesta 403 a un
              // `tech_member`, y el guard evita que la pantalla se monte para
              // pedir datos que no va a recibir.
              {
                path: 'metrics',
                element: <RequireRole allow={ADMIN_ONLY} />,
                children: [{ index: true, element: <MetricsPage /> }],
              },
            ],
          },

          // --- Portal ---------------------------------------------------
          {
            path: 'portal',
            element: (
              <RequireRole allow={PORTAL}>
                <PortalLayout />
              </RequireRole>
            ),
            children: portalRoutes,
          },
        ],
      },

      { path: '403', element: <ForbiddenPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export function createRouter() {
  return createBrowserRouter(routes)
}
